import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Activity,
    ArrowRight,
    Building2,
    Download,
    ChevronRight,
    GraduationCap,
    HeartHandshake,
    Map,
    MapPinned,
    Search,
    ShieldAlert,
    Sprout,
    TrendingUp,
    Users,
    UserRoundCheck,
    LogOut,
    X,
} from "lucide-react";
import {
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
} from "recharts";

import {
    ComposableMap,
    Geographies,
    Geography,
} from "react-simple-maps";
import { Input } from "@/components/ui/input";
import { Loader } from "@/components/common/Loader";
import { UserIdentity } from "@/components/layout/UserIdentity";
import { DOWNLOADS_ENABLED } from "@/config/features";
import * as XLSX from "@/lib/excelExport";
import { stateSummaryRowsForExcel } from "@/lib/excelExportFilters";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { canAccessState } from "@/auth/authorization";

const publicUrl = process.env.PUBLIC_URL || "";

const number = (value) =>
    Number.isFinite(Number(value))
        ? new Intl.NumberFormat("en-IN").format(Number(value))
        : "—";

const decimal = (value, digits = 1) =>
    Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : "—";

const percentage = (value, digits = 1) =>
    Number.isFinite(Number(value)) ? `${Number(value).toFixed(digits)}%` : "—";

const CHART_COLORS = ["#5B8DB8", "#C9583B", "#D27B0C", "#9A907E"];

const formatExportHeader = (key = "") =>
    key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (character) => character.toUpperCase());

const autoSizeWorksheet = (worksheet, rows) => {
    const keys = rows.length ? Object.keys(rows[0]) : [];
    worksheet["!cols"] = keys.map((key) => ({
        wch: Math.min(
            45,
            Math.max(
                formatExportHeader(key).length + 2,
                ...rows.map((row) => String(row[key] ?? "").length + 2),
            ),
        ),
    }));
};


const INDIA_GEO_URL = "https://raw.githubusercontent.com/nswamy14/geoJson/master/india.states.geo.json";

const NORMALIZE_MAP_NAME = (value = "") =>
    value
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

const MAP_DESTINATIONS = {
    "andhra pradesh": { slug: "andhra-pradesh" },
    assam: { slug: "assam-and-ne-states" },
    "arunachal pradesh": { slug: "assam-and-ne-states" },
    meghalaya: { slug: "assam-and-ne-states" },
    mizoram: { slug: "assam-and-ne-states" },
    nagaland: { slug: "assam-and-ne-states" },
    tripura: { slug: "assam-and-ne-states" },
    bihar: { slug: "bihar" },
    chhattisgarh: { slug: "chattisgarh" },
    chattisgarh: { slug: "chattisgarh" },
    delhi: { slug: "delhi-and-ncr" },
    "nct of delhi": { slug: "delhi-and-ncr" },
    goa: { slug: "goa" },
    gujarat: { slug: "gujarat" },
    haryana: { slug: "haryana-and-chandigarh" },
    chandigarh: { slug: "haryana-and-chandigarh" },
    "himachal pradesh": { slug: "himachal-pradesh" },
    jharkhand: { slug: "jharkhand" },
    kerala: { slug: "kerala" },
    "madhya pradesh": { slug: "madhya-pradesh" },
    manipur: { slug: "manipur" },
    odisha: { slug: "odisha" },
    orissa: { slug: "odisha" },
    punjab: { slug: "punjab" },
    rajasthan: { slug: "rajasthan" },
    sikkim: { slug: "sikkim" },
    telangana: { slug: "telangana" },
    "uttar pradesh": { slug: "uttar-pradesh" },
    uttarakhand: { slug: "uttarakhand" },
    uttaranchal: { slug: "uttarakhand" },
    "jammu and kashmir": { slug: "jammu-and-kashmir" },
    "jammu kashmir": { slug: "jammu-and-kashmir" },
    "tamil nadu": {
        title: "Tamil Nadu State Organisations",
        options: [
            { label: "Tamil Nadu (North)", slug: "tamilnadu-north" },
            { label: "Tamil Nadu (South)", slug: "tamilnadu-south" },
        ],
    },
    karnataka: {
        title: "Karnataka State Organisations",
        options: [
            { label: "Karnataka North", slug: "karnataka-north" },
            { label: "Karnataka South", slug: "karnataka-south" },
        ],
    },
    "west bengal": {
        title: "West Bengal State Organisations",
        options: [
            { label: "West Bengal (One)", slug: "west-bengal-one" },
            { label: "West Bengal (Two)", slug: "west-bengal-two" },
        ],
    },
    maharashtra: {
        title: "Maharashtra State Organisations",
        options: [
            { label: "Maharashtra East", slug: "maharashtra-east" },
            { label: "Maharashtra West", slug: "maharashtra-west" },
            { label: "Maharashtra West (One)", slug: "maharashtra-west-one" },
        ],
    },
};

const getMapStateName = (geo) =>
    geo?.properties?.ST_NM ||
    geo?.properties?.NAME_1 ||
    geo?.properties?.name ||
    geo?.properties?.NAME ||
    "";

const StateChoiceDialog = ({ selection, onClose, onSelect }) => {
    if (!selection) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm" onClick={onClose}>
            <div
                className="w-full max-w-md rounded-3xl border border-border/70 bg-card p-6 shadow-2xl"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="map-state-choice-title"
            >
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">Select organisation</p>
                        <h3 id="map-state-choice-title" className="mt-1 font-serif-elegant text-2xl text-foreground">
                            {selection.title}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground transition hover:bg-muted/80 hover:text-foreground"
                        aria-label="Close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="mt-5 space-y-3">
                    {selection.options.map((option) => (
                        <button
                            key={option.slug}
                            type="button"
                            onClick={() => onSelect(option.slug)}
                            className="flex w-full items-center justify-between rounded-2xl border border-border/70 bg-background px-4 py-4 text-left transition hover:border-primary/40 hover:bg-primary/5"
                        >
                            <span className="font-semibold text-foreground">{option.label}</span>
                            <ArrowRight className="h-4 w-4 text-primary" />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

const authorizedDestination = (destination, allowedStateSlugs) => {
    if (destination?.slug) return allowedStateSlugs.has(destination.slug) ? destination : null;
    const options = (destination?.options || []).filter((option) => allowedStateSlugs.has(option.slug));
    return options.length ? { ...destination, options } : null;
};

const IndiaStateMap = ({ onStateClick, allowedStateSlugs }) => (
    <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-white/10 p-3 backdrop-blur">
        <ComposableMap
            projection="geoMercator"
            projectionConfig={{ center: [82.5, 22.5], scale: 950 }}
            width={620}
            height={560}
            className="h-[340px] w-full sm:h-[420px]"
        >
            <Geographies geography={INDIA_GEO_URL}>
                {({ geographies }) =>
                    geographies.map((geo) => {
                        const stateName = getMapStateName(geo);
                        const destination = authorizedDestination(
                            MAP_DESTINATIONS[NORMALIZE_MAP_NAME(stateName)],
                            allowedStateSlugs,
                        );
                        const enabled = Boolean(destination);

                        return (
                            <Geography
                                key={geo.rsmKey}
                                geography={geo}
                                onClick={() => enabled && onStateClick(stateName, destination)}
                                tabIndex={enabled ? 0 : -1}
                                role={enabled ? "button" : undefined}
                                aria-label={enabled ? `Open ${stateName} dashboard` : `${stateName} is not mapped`}
                                onKeyDown={(event) => {
                                    if (enabled && (event.key === "Enter" || event.key === " ")) {
                                        event.preventDefault();
                                        onStateClick(stateName, destination);
                                    }
                                }}
                                style={{
                                    default: {
                                        fill: enabled ? "rgba(255,255,255,0.78)" : "rgba(255,255,255,0.20)",
                                        stroke: "rgba(15,23,42,0.55)",
                                        strokeWidth: 0.7,
                                        outline: "none",
                                        cursor: enabled ? "pointer" : "default",
                                    },
                                    hover: {
                                        fill: enabled ? "hsl(var(--primary))" : "rgba(255,255,255,0.20)",
                                        stroke: "rgba(255,255,255,0.9)",
                                        strokeWidth: 1.1,
                                        outline: "none",
                                        cursor: enabled ? "pointer" : "default",
                                    },
                                    pressed: {
                                        fill: enabled ? "hsl(var(--primary))" : "rgba(255,255,255,0.20)",
                                        stroke: "white",
                                        strokeWidth: 1.1,
                                        outline: "none",
                                    },
                                }}
                            >
                                <title>{enabled ? `${stateName} — click to open` : `${stateName} — no State Organisation mapped`}</title>
                            </Geography>
                        );
                    })
                }
            </Geographies>
        </ComposableMap>

        <div className="pointer-events-none absolute bottom-3 left-3 rounded-xl bg-slate-950/70 px-3 py-2 text-[10px] text-white/75 backdrop-blur">
            Hover over a state and click to open its dashboard
        </div>
    </div>
);

const SaiMark = () => (
    <img
        src={`${publicUrl}/images/sssso-logo.png`}
        alt="Sri Sathya Sai Seva Organisations"
        className="h-14 w-14 shrink-0 object-contain"
    />
);

const CoverageItem = ({ label, value }) => (
    <div className="min-w-0 border-r border-white/15 px-4 last:border-r-0 sm:px-6">
        <p className="font-serif-elegant text-2xl text-white sm:text-3xl">{number(value)}</p>
        <p className="mt-1 text-[11px] uppercase tracking-[0.13em] text-white/65">{label}</p>
    </div>
);

const KpiTile = ({ icon: Icon, label, value, note }) => (
    <div className="group rounded-2xl border border-border/70 bg-card p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
                <p className="mt-3 font-serif-elegant text-2xl text-foreground sm:text-3xl">{value}</p>
                <p className="mt-1 truncate text-xs text-muted-foreground">{note}</p>
            </div>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" strokeWidth={1.8} />
            </span>
        </div>
    </div>
);

const MiniDonut = ({ title, centerLabel, centerValue, data }) => (
    <div className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">{title}</p>
        <div className="mt-3 grid items-center gap-4 sm:grid-cols-[145px_1fr]">
            <div className="relative h-36">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={42}
                            outerRadius={62}
                            paddingAngle={2}
                            stroke="hsl(var(--card))"
                            strokeWidth={3}
                        >
                            {data.map((entry, index) => (
                                <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => number(value)} />
                    </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{centerLabel}</span>
                    <span className="font-serif-elegant text-lg text-foreground">{number(centerValue)}</span>
                </div>
            </div>
            <div className="space-y-3">
                {data.map((entry, index) => (
                    <div key={entry.name} className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                            <span
                                className="h-2.5 w-2.5 shrink-0 rounded-full"
                                style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                            />
                            <span className="truncate text-xs text-muted-foreground">{entry.name}</span>
                        </div>
                        <span className="text-sm font-semibold text-foreground">{number(entry.value)}</span>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const StateMetric = ({ label, value, emphasis = false }) => (
    <div className="min-w-0">
        <p className="truncate text-[9px] font-semibold uppercase tracking-[0.11em] text-muted-foreground">{label}</p>
        <p className={`mt-1 truncate text-sm ${emphasis ? "font-bold text-primary" : "font-semibold text-foreground"}`}>
            {value}
        </p>
    </div>
);

const RankList = ({ title, states, valueKey, formatter = number }) => (
    <div className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
            <h3 className="font-serif-elegant text-lg text-foreground">{title}</h3>
            <TrendingUp className="h-4 w-4 text-primary" />
        </div>
        <div className="mt-4 space-y-3">
            {states.map((state, index) => (
                <Link
                    key={state.state_id}
                    to={`/?state=${encodeURIComponent(state.slug)}`}
                    className="flex items-center justify-between gap-3 rounded-xl px-2 py-2 transition hover:bg-muted/55"
                >
                    <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                            {index + 1}
                        </span>
                        <span className="truncate text-sm text-foreground">{state.state_name.trim()}</span>
                    </div>
                    <span className="shrink-0 text-sm font-semibold text-foreground">{formatter(state[valueKey])}</span>
                </Link>
            ))}
        </div>
    </div>
);

export default function NationalDashboard() {
    const { user, logout } = useAuth();
    const [manifest, setManifest] = useState(null);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [selectedStateSlug, setSelectedStateSlug] = useState(null);
    const [mapSelection, setMapSelection] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = user.scopeType === "ZONE" ? "SSSSO India | Zonal Insights" : "SSSSO India | National Dashboard";
        apiFetch("/api/dashboard/national")
            .then(setManifest)
            .catch((e) => setError(e.message));
    }, [user.scopeType]);

    const states = useMemo(
        () => (manifest?.states || []).filter((state) => canAccessState(user, state)),
        [manifest, user],
    );

    const allowedStateSlugs = useMemo(
        () => new Set(states.map((state) => state.slug)),
        [states],
    );
    const isZone = user.scopeType === "ZONE";
    const reportingPeriod = manifest?.reporting_period?.period_label || "Reporting period unavailable";

    const national = manifest?.national_totals || {};

    const filtered = useMemo(() => {
        const term = query.trim().toLowerCase();
        const result = !term
            ? [...states]
            : states.filter(
                  (state) =>
                      state.state_name.toLowerCase().includes(term) ||
                      state.slug.toLowerCase().includes(term),
              );

        return result.sort((a, b) => {
            if (sortBy === "members") return Number(b.members || 0) - Number(a.members || 0);
            if (sortBy === "youth") return Number(b.youth_share_pct || 0) - Number(a.youth_share_pct || 0);
            if (sortBy === "activities") return Number(b.activities || 0) - Number(a.activities || 0);
            if (sortBy === "beneficiaries") return Number(b.service_beneficiaries || 0) - Number(a.service_beneficiaries || 0);
            if (sortBy === "vacancy") return Number(b.leadership_vacancy_rate_pct || 0) - Number(a.leadership_vacancy_rate_pct || 0);
            if (sortBy === "district-gap") return Number(b.districts_with_less_than_5_samithis || 0) - Number(a.districts_with_less_than_5_samithis || 0);
            return a.state_name.localeCompare(b.state_name);
        });
    }, [states, query, sortBy]);

    useEffect(() => {
        if (!filtered.length) {
            setSelectedStateSlug(null);
            return;
        }

        const selectedStillVisible = filtered.some(
            (state) => state.slug === selectedStateSlug,
        );

        if (!selectedStillVisible) {
            setSelectedStateSlug(filtered[0].slug);
        }
    }, [filtered, selectedStateSlug]);

    const selectedState =
        filtered.find((state) => state.slug === selectedStateSlug) ||
        filtered[0] ||
        null;

    const membershipTotal = Number(national.members || 0);
    const youthTotal = Number(national.approved_youth_members || 0);
    const seniorTotal = Number(national.senior_members || Math.max(membershipTotal - youthTotal, 0));

    const kpis = [
        { icon: Users, label: "Approved Members", value: number(national.members), note: "Total approved membership" },
        { icon: Sprout, label: "Youth Members", value: number(national.approved_youth_members), note: `${percentage(national.youth_share_pct)} of members` },
        { icon: GraduationCap, label: "Bal Vikas Gurus", value: number(national.bal_vikas_gurus), note: "Across State Organisations" },
        { icon: MapPinned, label: "Adopted Villages", value: number(national.adopted_villages), note: `${number(national.tribal_villages)} tribal villages` },
        { icon: Building2, label: "Vidya Jyothi Schools", value: number(national.vidya_jyothi_schools), note: "Samithi Level Schools" },
        { icon: Activity, label: "Reported Activities", value: number(national.activities), note: "Approved reports" },
        { icon: HeartHandshake, label: "Beneficiaries", value: number(national.service_beneficiaries), note: "Service beneficiaries" },
        { icon: UserRoundCheck, label: "Average Attendance", value: decimal(national.average_attendance_per_activity), note: "Per reported activity" },
    ];

    const chartData = [
        {
            title: "Gender Composition",
            centerLabel: "Members",
            centerValue: membershipTotal,
            data: [
                { name: "Gents", value: Number(national.gents_members || 0) },
                { name: "Ladies", value: Number(national.ladies_members || 0) },
            ],
        },
        {
            title: "Youth vs Seniors",
            centerLabel: "Youth",
            centerValue: youthTotal,
            data: [
                { name: "Youth", value: youthTotal },
                { name: "Seniors", value: seniorTotal },
            ],
        },
        {
            title: "Youth by Gender",
            centerLabel: "Youth",
            centerValue: youthTotal,
            data: [
                { name: "Youth Gents", value: Number(national.gents_youth || 0) },
                { name: "Youth Ladies", value: Number(national.ladies_youth || 0) },
            ],
        },
    ];

    const topMembers = [...states].sort((a, b) => Number(b.members || 0) - Number(a.members || 0)).slice(0, 5);
    const attentionStates = [...states]
        .sort((a, b) => Number(b.leadership_vacancy_rate_pct || 0) - Number(a.leadership_vacancy_rate_pct || 0))
        .slice(0, 5);


    const openMappedState = (stateName, destination) => {
        if (destination?.slug) {
            navigate(`/?state=${encodeURIComponent(destination.slug)}`);
            return;
        }

        if (destination?.options?.length) {
            setMapSelection({
                title: destination.title || stateName,
                options: destination.options,
            });
        }
    };

    const selectMappedOrganisation = (slug) => {
        setMapSelection(null);
        navigate(`/?state=${encodeURIComponent(slug)}`);
    };

    const downloadAllData = async () => {
        if (!DOWNLOADS_ENABLED || !manifest) return;

        const generatedAt = new Date();
        const overviewLabel = isZone ? "Zonal" : "National";
        const workbook = XLSX.utils.book_new();

        const nationalRows = Object.entries(national).map(([metric, value]) => ({
            Metric: formatExportHeader(metric),
            Value: value ?? "",
        }));

        nationalRows.unshift(
            { Metric: "Report", Value: `SSSSO India ${overviewLabel} Dashboard` },
            { Metric: "Reporting Period", Value: "2025-26" },
        );

        const exportStates = stateSummaryRowsForExcel(states);
        const stateKeys = Array.from(
            new Set(exportStates.flatMap((state) => Object.keys(state))),
        );

        const stateRows = exportStates.map((state) =>
            Object.fromEntries(
                stateKeys.map((key) => [formatExportHeader(key), state[key] ?? ""]),
            ),
        );

        const nationalSheet = XLSX.utils.json_to_sheet(nationalRows);
        autoSizeWorksheet(nationalSheet, nationalRows);
        XLSX.utils.book_append_sheet(workbook, nationalSheet, `${overviewLabel} Summary`);

        const statesSheet = XLSX.utils.json_to_sheet(stateRows);
        autoSizeWorksheet(statesSheet, stateRows);
        XLSX.utils.book_append_sheet(workbook, statesSheet, "State Organisations");

        await XLSX.writeFile(
            workbook,
            `SSSSO_India_${overviewLabel}_Dashboard_${generatedAt.toISOString().slice(0, 10)}.xlsx`,
        );
    };

    if (!manifest && !error) return <Loader />;

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border/70 bg-card/95 backdrop-blur">
                <div className="mx-auto flex max-w-[1680px] items-center justify-between gap-4 px-5 py-4 sm:px-8">
                    <div className="flex min-w-0 items-center gap-4">
                        <SaiMark />
                        <div className="min-w-0">
                            <p className="truncate font-serif-elegant text-xl leading-tight text-foreground">Sri Sathya Sai Seva Organisations</p>
                            <p className="text-sm text-muted-foreground">India</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="hidden rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.13em] text-primary sm:inline-flex">
                            {reportingPeriod}
                        </span>
                        {DOWNLOADS_ENABLED && (
                            <button
                                type="button"
                                onClick={downloadAllData}
                                disabled={!manifest}
                                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                                title="Download all national and state-level data"
                            >
                                <Download className="h-4 w-4" />
                                <span className="hidden sm:inline">Download All Data</span>
                                <span className="sm:hidden">Excel</span>
                            </button>
                        )}
                        <UserIdentity />
                        <button
                            type="button"
                            onClick={logout}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border/70 bg-card text-muted-foreground transition hover:bg-muted hover:text-foreground"
                            title="Log out"
                            aria-label="Log out"
                        >
                            <LogOut className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-[1680px] px-5 py-7 sm:px-8 lg:py-9">
                <section className="relative overflow-hidden rounded-[30px] bg-gradient-to-br from-slate-950 via-slate-900 to-primary/80 shadow-soft">
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-y-0 left-0 hidden w-[36%] bg-contain bg-left-bottom bg-no-repeat opacity-[0.10] mix-blend-screen lg:block"
                        style={{ backgroundImage: `url(${publicUrl}/images/swami-silhouette.png)` }}
                    />
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute left-[-8%] top-[-10%] hidden h-[22rem] w-[22rem] rounded-full bg-white/8 blur-3xl lg:block"
                    />
                    <div className="grid gap-8 px-7 py-8 sm:px-10 sm:py-10 lg:grid-cols-[1.25fr_0.75fr] lg:items-center">
                        <div className="relative z-10">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">
                                {isZone ? "Zonal Insights" : "National Command Centre"}
                            </p>
                            <h1 className="mt-3 max-w-4xl font-serif-elegant text-3xl leading-tight text-white sm:text-5xl">
                                One view of organisational reach, participation and performance.
                            </h1>
                            <p className="mt-4 max-w-3xl text-sm leading-6 text-white/70 sm:text-base">
                                {isZone
                                    ? "A zonal overview with direct access to every authorised State Organisation and its district-level performance."
                                    : "A compact national overview with direct access to every State Organisation and its district-level performance."}
                            </p>
                        </div>
                        <div className="relative z-10">
                            <div className="mb-3 flex items-center justify-between gap-3 text-white">
                                <div className="flex items-center gap-3">
                                    <Map className="h-5 w-5" />
                                    <div>
                                        <p className="text-sm font-semibold">India State Explorer</p>
                                        <p className="text-xs text-white/60">Click a state to open its State Organisation</p>
                                    </div>
                                </div>
                            </div>
                            <IndiaStateMap onStateClick={openMappedState} allowedStateSlugs={allowedStateSlugs} />
                            <div className="mt-3 flex flex-wrap gap-2">
                                {[
                                    ["Delhi & NCR", "delhi-and-ncr"],
                                    ["Haryana & Chandigarh", "haryana-and-chandigarh"],
                                    ["Assam & NE States", "assam-and-ne-states"],
                                ].filter(([, slug]) => allowedStateSlugs.has(slug)).map(([label, slug]) => (
                                    <button
                                        key={slug}
                                        type="button"
                                        onClick={() => navigate(`/?state=${encodeURIComponent(slug)}`)}
                                        className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[11px] font-semibold text-white/80 transition hover:bg-white/20 hover:text-white"
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 border-t border-white/15 bg-black/15 py-5 sm:grid-cols-4">
                        <CoverageItem label="State Organisations" value={national.state_organisations} />
                        <CoverageItem label="Districts" value={national.districts} />
                        <CoverageItem label="Samithis" value={national.samithis} />
                        <CoverageItem label="Bhajan Mandalis" value={national.bhajan_mandalis} />
                    </div>
                </section>

                {error ? (
                    <div className="mt-7 rounded-2xl border border-destructive/30 bg-destructive/5 p-5 text-sm text-destructive">{error}</div>
                ) : (
                    <>
                        <section className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                            {kpis.map((item) => <KpiTile key={item.label} {...item} />)}
                        </section>

                        <section className="mt-6 grid gap-5 lg:grid-cols-3">
                            {chartData.map((chart) => <MiniDonut key={chart.title} {...chart} />)}
                        </section>

                        <section className="mt-8">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">State Explorer</p>
                                    <h2 className="mt-1 font-serif-elegant text-2xl text-foreground">Explore State Organisations</h2>
                                    <p className="mt-1 text-sm text-muted-foreground">
                                        Select a State Organisation to review its key metrics before opening the detailed dashboard.
                                    </p>
                                </div>
                            </div>

                            <div className="mt-5 grid gap-6 xl:grid-cols-[minmax(270px,30%)_1fr]">
                                <div className="rounded-3xl border border-border/70 bg-card p-4 shadow-sm">
                                    <div className="space-y-3">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                            <Input
                                                value={query}
                                                onChange={(event) => setQuery(event.target.value)}
                                                placeholder="Search State Organisation"
                                                className="pl-9"
                                            />
                                        </div>

                                        <select
                                            value={sortBy}
                                            onChange={(event) => setSortBy(event.target.value)}
                                            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
                                        >
                                            <option value="name">Sort: State name</option>
                                            <option value="members">Sort: Members</option>
                                            <option value="youth">Sort: Youth share</option>
                                            <option value="activities">Sort: Activities</option>
                                            <option value="beneficiaries">Sort: Beneficiaries</option>
                                            <option value="vacancy">Sort: Vacancy rate</option>
                                            <option value="district-gap">Sort: Districts &lt;5 Samithis</option>
                                        </select>
                                    </div>

                                    <div className="mt-4 max-h-[720px] space-y-2 overflow-y-auto pr-1">
                                        {filtered.map((state) => {
                                            const selected = state.slug === selectedState?.slug;

                                            return (
                                                <button
                                                    key={state.state_id}
                                                    type="button"
                                                    onClick={() => setSelectedStateSlug(state.slug)}
                                                    className={`group flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                                                        selected
                                                            ? "border-primary/40 bg-primary/8 shadow-sm"
                                                            : "border-transparent bg-muted/35 hover:border-border hover:bg-muted/65"
                                                    }`}
                                                >
                                                    <div className="min-w-0">
                                                        <p className={`truncate text-sm font-semibold ${selected ? "text-primary" : "text-foreground"}`}>
                                                            {state.state_name.trim()}
                                                        </p>
                                                        <p className="mt-1 truncate text-[11px] text-muted-foreground">
                                                            {number(state.districts)} districts · {number(state.samithis)} samithis
                                                        </p>
                                                    </div>
                                                    <ChevronRight className={`h-4 w-4 shrink-0 transition ${selected ? "text-primary" : "text-muted-foreground group-hover:text-foreground"}`} />
                                                </button>
                                            );
                                        })}

                                        {!filtered.length && (
                                            <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                                                No state matches “{query}”.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="min-w-0">
                                    {selectedState ? (
                                        <div className="overflow-hidden rounded-3xl border border-border/70 bg-card shadow-sm">
                                            <div className="border-b border-border/70 bg-gradient-to-br from-primary/10 via-background to-background px-6 py-6 sm:px-8">
                                                <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                                                    <div>
                                                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">Selected State Organisation</p>
                                                        <h3 className="mt-2 font-serif-elegant text-3xl text-foreground">
                                                            {selectedState.state_name.trim()}
                                                        </h3>
                                                        <p className="mt-2 text-sm text-muted-foreground">
                                                            {number(selectedState.districts)} Districts · {number(selectedState.samithis)} Samithis · {number(selectedState.bhajan_mandalis)} Bhajan Mandalis
                                                        </p>
                                                    </div>

                                                    <Link
                                                        to={`/?state=${encodeURIComponent(selectedState.slug)}`}
                                                        className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm transition hover:opacity-90"
                                                    >
                                                        Explore State Dashboard
                                                        <ArrowRight className="h-4 w-4" />
                                                    </Link>
                                                </div>
                                            </div>

                                            <div className="p-6 sm:p-8">
                                                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                                    {[
                                                        ["Approved Members", number(selectedState.members)],
                                                        ["Youth Members", `${number(selectedState.approved_youth_members)} · ${percentage(selectedState.youth_share_pct)}`],
                                                        ["Bal Vikas Gurus", number(selectedState.bal_vikas_gurus)],
                                                        ["Reported Activities", number(selectedState.activities)],
                                                        ["Service Beneficiaries", number(selectedState.service_beneficiaries)],
                                                        ["Average Attendance", decimal(selectedState.average_attendance_per_activity)],
                                                        ["Adopted Villages", number(selectedState.adopted_villages)],
                                                        ["Vidya Jyothi Schools", number(selectedState.vidya_jyothi_schools)],
                                                        ["Vacant Positions", number(selectedState.vacant_positions)],
                                                        ["Vacancy Rate", percentage(selectedState.leadership_vacancy_rate_pct, 2)],
                                                        ["Districts <5 Samithis", `${number(selectedState.districts_with_less_than_5_samithis)} of ${number(selectedState.districts)}`],
                                                    ].map(([label, value]) => (
                                                        <div key={label} className="rounded-2xl border border-border/60 bg-muted/25 p-4">
                                                            <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-muted-foreground">{label}</p>
                                                            <p className="mt-2 font-serif-elegant text-2xl text-foreground">{value}</p>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="mt-6 grid gap-4 lg:grid-cols-2">
                                                    <div className="rounded-2xl border border-primary/15 bg-primary/5 p-5">
                                                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">Organisation Reach</p>
                                                        <div className="mt-4 grid grid-cols-3 gap-4">
                                                            <StateMetric label="Districts" value={number(selectedState.districts)} />
                                                            <StateMetric label="Samithis" value={number(selectedState.samithis)} />
                                                            <StateMetric label="Bhajan Mandalis" value={number(selectedState.bhajan_mandalis)} />
                                                        </div>
                                                    </div>

                                                    <div className="rounded-2xl border border-amber-200/70 bg-amber-50/60 p-5 dark:border-amber-900/40 dark:bg-amber-950/20">
                                                        <div className="flex items-start gap-3">
                                                            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
                                                            <div>
                                                                <p className="font-semibold text-foreground">Structural attention</p>
                                                                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                                                                    {number(selectedState.districts_with_less_than_5_samithis)} of {number(selectedState.districts)} districts have fewer than five Samithis. Bhajan Mandalis are excluded.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex min-h-[420px] items-center justify-center rounded-3xl border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground">
                                            Select a State Organisation to view its performance summary.
                                        </div>
                                    )}

                                    <div className="mt-6 grid gap-5 lg:grid-cols-2">
                                        <RankList title="Largest Membership" states={topMembers} valueKey="members" />
                                        <RankList
                                            title="Leadership Attention"
                                            states={attentionStates}
                                            valueKey="leadership_vacancy_rate_pct"
                                            formatter={(value) => percentage(value, 1)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>
                    </>
                )}
            </main>

            <footer className="mx-auto max-w-[1680px] px-5 pb-10 pt-4 text-center text-xs text-muted-foreground sm:px-8">
                Love All · Serve All
            </footer>

            <StateChoiceDialog
                selection={mapSelection}
                onClose={() => setMapSelection(null)}
                onSelect={selectMappedOrganisation}
            />
        </div>
    );
}
